import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import { SetContextLink } from '@apollo/client/link/context';
import environment from '@lib/environment';
import WebSocket from 'ws';

import AuthModule from '../modules/AuthModule';
import { MockLink } from '@apollo/client/testing';
import retryLink from './lib/retryLink';
import elevateGqlErrorsLink from './lib/elevateErrorsLink';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { readStickyPath } from '@modules/AuthModule/lib/authPath';
import { redirectToBffLogin } from '@modules/AuthModule/lib/bffSession';

import graphql from './generated/graphql';
import loggingFetch from './lib/loggingFetch';

const { API_URL } = environment;
export const MAX_RETRY_ATTEMPTS = 5;

const isWebSocketUnavailable =
  typeof window === 'undefined' || typeof window.WebSocket === 'undefined';

const wsLink = new GraphQLWsLink(
  createClient({
    url: API_URL.replace(/^http/, 'ws') + '/graphql',
    connectionParams: async () => {
      // Per-connection auth-path check: session path relies on the cookie riding
      // the same-host WS upgrade — no Authorization header needed. PKCE path
      // sends the bearer token as today.
      const authPath = readStickyPath();
      if (authPath === 'session') {
        return {};
      }
      const token = await AuthModule.getCurrentToken();
      return {
        Authorization: `Bearer ${token}`,
      };
    },
    retryAttempts: MAX_RETRY_ATTEMPTS,
    webSocketImpl: isWebSocketUnavailable ? WebSocket : undefined,
  }),
);

const httpLink = new UploadHttpLink({
  fetch: loggingFetch,
  uri: ({ operationName }) => `${API_URL}/graphql?opname=${operationName}`,
  headers: { 'GraphQL-preflight': '1' },
});

export const authLink = new SetContextLink(async ({ headers }) => {
  // Use readStickyPath() synchronously — selectAuthPath() creates LD client
  // instances on every call, causing memory exhaustion on page load.
  const authPath = readStickyPath();
  const serviceId = AuthModule.getCurrentServiceId();

  const sharedHeaders: Record<string, string> = {
    ...headers,
    'X-ContactCenter-Id': serviceId,
  };

  if (authPath === 'session') {
    return {
      credentials: 'include',
      headers: {
        ...sharedHeaders,
        'X-CSRF': '1',
      },
    };
  }

  // PKCE path — bearer token, no X-CSRF, no credentials
  const token = (await AuthModule.getCurrentToken()) || '';
  return {
    headers: {
      ...sharedHeaders,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

/**
 * 401 / unauthenticated error link — session path only.
 *
 * When the BFF returns 401 (e.g. stale tab, cross-tab logout), restart sign-in
 * via /bff/login. No PKCE refresh is attempted. The SPA is never wedged by a
 * path flip because selectAuthPath() is re-evaluated at call time.
 *
 * On the PKCE path this link is a no-op — existing retry/refresh behavior is
 * preserved by the retryLink and authLink upstream.
 */
const bff401Link = onError(({ error }) => {
  // error.statusCode is set by Apollo when the HTTP response is non-2xx.
  // Cast through unknown to Record so the bracket access compiles without
  // the SonarQube S4325 flag that (error as any) triggers.
  const statusCode = (error as unknown as Record<string, unknown>)?.[
    'statusCode'
  ];
  if (statusCode !== 401) return;

  // Use readStickyPath() synchronously — selectAuthPath() creates two LD client
  // instances per call (kill-switch + rollout), causing memory exhaustion when
  // called on every 401.
  if (readStickyPath() !== 'session') {
    // PKCE path: this link is a pure no-op. Returning here (undefined) lets the
    // error propagate to the existing retryLink/refresh chain exactly as before.
    // NEVER call forward(operation) here — bff401Link is the first link in the
    // chain, so forwarding re-runs the request through itself and loops forever
    // on a persistent 401, allocating observables until the tab OOMs.
    return;
  }

  // Session path only: a 401 means the BFF session is gone (stale tab,
  // cross-tab logout). Restart sign-in via a top-level redirect to /bff/login.
  redirectToBffLogin();
});

const httpLinkWithMiddleware = ApolloLink.from([
  bff401Link,
  retryLink(MAX_RETRY_ATTEMPTS),
  elevateGqlErrorsLink,
  authLink,
  httpLink,
]);

const splitLink = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLinkWithMiddleware,
);

let globalMocks: readonly MockLink.MockedResponse<any, any>[] | null = null;
export function setMocks(
  mocks: readonly MockLink.MockedResponse<any, any>[] = [],
) {
  globalMocks = mocks;
}

const client = new ApolloClient({
  link: splitLink,
  cache: createCache(),
});

export function getClient() {
  if (globalMocks) {
    return new ApolloClient({
      link: new MockLink(globalMocks),
      cache: createCache(),
    });
  }
  return client;
}

export function createCache() {
  return new InMemoryCache({
    typePolicies: {
      User: {
        keyFields: ['id'],
        merge: true,
        fields: {
          personalLine: {
            merge: true,
          },
        },
      },
      UserPersonalLine: {
        keyFields: false,
      },
      Address: {
        keyFields: false,
      },
      /**
       * Customer merge strategy for multi-instance queries
       *
       * Problem: Same customer query used across multiple sections in customer record (locations, info, contacts)
       * causes cache overwrites where each query instance only returns partial data,
       * resulting in loss of previously fetched fields.
       * Enable merge:true to accumulate all fields from different query responses
       * into a single customer cache entry, preserving data from all query instances.
       */
      Customer: {
        merge: true,
      },
      CallReason: {
        keyFields: ['id', 'tenantId'],
      },
      CalendarRuleSet: {
        keyFields: ['id'],
        fields: {
          rules: {
            merge: false,
          },
        },
      },
      VAAgent: {
        // VA Agent on Follow Ups / Conversations can have different name even if they have the
        // same ID (the name can be changed in the settings).
        // Since there is no way for cache to differentiate between them
        // we should disable keyFields for this type
        keyFields: false,
      },
      AuditInstigator: {
        keyFields: false,
      },
    },
    possibleTypes: graphql.possibleTypes,
  });
}
